import React, { ButtonHTMLAttributes, forwardRef, useEffect, useState, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import { cn } from '../utils/common';
import { dispatchSelectOpen, onSelectOpen } from '../utils/event-bus';

export type SelectOption = {
  label: string;
  value: string;
};

export interface SelectProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof selectVariants> {
  options: SelectOption[];
  icon?: React.ReactNode;
  defaultOption?: SelectOption;
  onSelect: (opt: Record<string, any>) => void;
  dropdownClassName?: string;
}

const selectVariants = cva(
  'flex items-center justify-between gap-2 w-full p-3 appearance-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-brand-hover border border-gray-700 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20',
        destructive: 'bg-red-500/10 text-white border-red-500',
        outline: 'border border-gray-700 bg-transparent text-neutral-light hover:border-accent',
        secondary: 'bg-brand-surface text-neutral-light hover:bg-brand-hover',
        ghost: 'bg-transparent text-neutral-light hover:bg-brand-hover',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ className, defaultOption, icon, options, variant, onSelect, dropdownClassName = "text-text-muted bg-brand-surface", ...props }, ref) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>(defaultOption);
    const id = useId();

    const toggleDropdown = () => {
      if (!isOpen) {
        dispatchSelectOpen(id);
      }
      setIsOpen(!isOpen);
    };

    const handleOptionSelect = (option: SelectOption) => {
      setSelectedOption(option);
      setIsOpen(false);
      onSelect(option);
    };

    useEffect(() => {
      const unsubscribe = onSelectOpen((openId) => {
        if (openId !== id) {
          setIsOpen(false);
        }
      });

      return () => {
        unsubscribe();
      };
    }, [id]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const targetElement = event.target as HTMLElement;
        // Escape the ID for use in a CSS selector
        const escapedId = id.replace(/:/g, '\\:');
        
        if (isOpen && !targetElement.closest(`#select-container-${escapedId}`)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, id]);

    useEffect(() => {
      setSelectedOption(defaultOption);
    }, [defaultOption]);

    return (
      <div id={`select-container-${id}`} ref={ref} className="select relative">
        <button
          {...props}
          type="button"
          className={cn(selectVariants({ variant, className }))}
          onClick={toggleDropdown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`select-list-${id}`}
        >
          <div className="flex items-center gap-2">
            {icon && icon}
            {selectedOption?.label ?? <p className="text-text-muted">Select an option</p>}
          </div>
          <ChevronDown className="w-5 h-5 text-text-muted pointer-events-none" />
        </button>
        {isOpen && (
          <ul
            id={`select-list-${id}`}
            role="listbox"
            className={cn(
              "z-50 absolute w-full border border-accent/50 ring-1 ring-accent/20 rounded-md shadow-md",
              "max-h-[200px] overflow-y-auto"
            )}
          >
            {options.map((option) => (
              <li
                key={option.value}
                role="option"
                className={cn(
                  "px-4 py-2 cursor-pointer hover:text-neutral-light hover:bg-brand-hover",
                  dropdownClassName
                )}
                onClick={() => handleOptionSelect(option)}
                aria-selected={selectedOption?.value === option.value}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);
Select.displayName = 'Select';

export { Select, selectVariants };
